import { useMutation } from '@apollo/client';
import { UPDATE_CREATIVE } from '../../graphql/mutations/creativeMutations';

/**
 * Hook to update an existing Creative profile
 * @returns {object} - { handleUpdateCreative, loading, error }
 */
export const useUpdateCreative = () => {
  const [updateCreativeMutation, { loading, error }] = useMutation(UPDATE_CREATIVE, {
    refetchQueries: ['GetCreative', 'CreativesByUserID'],
    awaitRefetchQueries: true
  });

  const sanitizeInput = (input = {}) => {
    const cleaned = { ...input };
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === undefined) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  const handleUpdateCreative = async (creativeData) => {
    try {
      if (!creativeData?.id) {
        throw new Error('Das Creative-Profil konnte nicht zugeordnet werden. Bitte lade die Seite neu und versuche es erneut.');
      }

      const input = sanitizeInput({
        id: creativeData.id,
        creativeType: creativeData.creativeType,
        visualTypes: creativeData.visualTypes,
        // Allow status to be set for manual deactivation (INACTIVE)
        // Publishing should use publishCreative mutation
        status: creativeData.status,
        profilePicture: creativeData.profilePicture,
        displayName: creativeData.displayName,
        jobTitle: creativeData.jobTitle,
        description: creativeData.description,
        skills: creativeData.skills,
        // These are usually computed/auto-updated
        projectCount: creativeData.projectCount,
        packageCount: creativeData.packageCount,
        viewCount: creativeData.viewCount
      });

      const variables = sanitizeInput({
        input,
        condition: creativeData?.condition
      });

      const { data: creativeResponse } = await updateCreativeMutation({
        variables
      });

      return creativeResponse?.updateCreative;
    } catch (error) {
      console.error('Error updating creative:', error);
      throw error;
    }
  };

  // Note: For publishing, use publishCreative mutation
  // For deactivating, you can set status: 'INACTIVE' directly
  // Backend also handles status automatically based on packages/projects

  return {
    handleUpdateCreative,
    loading,
    error
  };
};
